import { useNavigate } from '@tanstack/react-router';
import { markDecouverteSeen } from '@/features/decouverte/decouverte-seen';
import { DecouverteCarousel } from '@/features/decouverte/decouverte-carousel';
import { DecouverteAutoplayer } from '@/features/decouverte/decouverte-autoplayer';
import {
  saveDecouverteProfile,
  type DecouverteProfile,
} from '@/features/decouverte/decouverte-profile';
import { trackAppEvent } from '@/lib/analytics/track';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

/** Segment de statistiques du profil choisi (`/decouverte/eleve`…). */
const PROFILE_SLUG: Record<DecouverteProfile, string> = {
  student: 'eleve',
  parent: 'parent',
  tutor: 'prof',
  player: 'curieux',
};

/**
 * Route `/decouverte` — présentation de l'app AVANT la connexion, au tout premier
 * lancement (cf. `indexRoute` dans `router.tsx`). Publique, aucune auth.
 *
 * Trois routes voisines, trois moments distincts du parcours :
 *   /decouverte → ici. Sans compte, avant le login. Accueil + parcours par profil.
 *   /onboarding → après la 1re connexion. Création du profil (rôle, niveau, objectifs).
 *   /bienvenue  → retour de paiement Stripe (`?session_id=`). Rien à voir avec l'accueil.
 *
 * Trois sorties : inscription (`/login?inscription=1`, profil mémorisé → titre
 * adapté, rôle présélectionné à l'onboarding), connexion (`/login`), démo sans
 * compte (`/demo`).
 */
export function DecouvertePage() {
  const navigate = useNavigate();

  function pickProfile(profile: DecouverteProfile) {
    saveDecouverteProfile(profile);
    // page « virtuelle » : le choix du profil apparaît dans les statistiques
    trackAppEvent('page_view', `/decouverte/${PROFILE_SLUG[profile]}`);
  }

  function goToSignup(profile: DecouverteProfile) {
    saveDecouverteProfile(profile);
    markDecouverteSeen();
    void navigate({ to: '/login', search: { inscription: 1 } });
  }

  function goToLogin() {
    markDecouverteSeen();
    void navigate({ to: '/login' });
  }

  function goToDemo(profile: DecouverteProfile) {
    saveDecouverteProfile(profile);
    markDecouverteSeen();
    void navigate({ to: '/demo' });
  }

  return (
    // Mobile (< lg) : colonne unique — l'accueil fait toute la hauteur.
    // Desktop (≥ lg) : deux moitiés plein écran — aperçu du player sur fond BLANC
    // à gauche, accueil sur fond bleu nuit à droite.
    <div className="min-h-dvh bg-[#061433] lg:grid lg:h-dvh lg:grid-cols-2">
      {/* Fond blanc LITTÉRAL (inline) : en thème océan `bg-white` = surface
          translucide, il laisserait passer le bleu. */}
      <div
        className="hidden lg:flex lg:h-dvh lg:items-center lg:justify-center lg:overflow-hidden lg:px-8"
        style={{ background: 'linear-gradient(165deg, #ffffff 0%, #eff4fb 100%)' }}
      >
        <DecouverteAutoplayer />
      </div>

      <div className="flex min-h-dvh items-center justify-center lg:h-dvh lg:min-h-0 lg:overflow-hidden">
        <DecouverteCarousel
          onSignup={goToSignup}
          onLogin={goToLogin}
          onDemo={goToDemo}
          onPickProfile={pickProfile}
          marketingUrl={`${MARKETING_URL}/`}
        />
      </div>
    </div>
  );
}
